import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ScorecardView({ user, onLogout }) {
  const { scorecardId } = useParams();
  const navigate = useNavigate();
  const [scorecard, setScorecard] = useState(null);
  const [corretor, setCorretor] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadScorecard();
  }, [scorecardId]);

  const loadScorecard = async () => {
    try {
      const scorecardRes = await axios.get(`${API}/scorecard/${scorecardId}`);
      setScorecard(scorecardRes.data);
      
      // Buscar informações do corretor
      const corretorRes = await axios.get(`${API}/corretores/${scorecardRes.data.corretor_id}`);
      setCorretor(corretorRes.data);
    } catch (error) {
      toast.error('Erro ao carregar scorecard');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!scorecard) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <Button
              variant="ghost"
              onClick={() => navigate(-1)}
              className="mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
            <h1 className="text-2xl font-bold text-blue-700">
              Ficha Semanal - {corretor?.nome}
            </h1>
            <p className="text-sm text-gray-600">
              Semana {scorecard.semana} | Módulo {scorecard.modulo}
            </p>
          </div>
          <span className={`px-4 py-2 text-sm font-semibold rounded-full ${
            scorecard.resumo?.decisao === 'Normal' ? 'bg-green-100 text-green-800' :
            scorecard.resumo?.decisao === 'Alerta Amarelo' ? 'bg-yellow-100 text-yellow-800' :
            scorecard.resumo?.decisao === 'Alerta Vermelho' ? 'bg-red-100 text-red-800' :
            'bg-gray-100 text-gray-800'
          }`}>
            {scorecard.resumo?.decisao || 'Normal'}
          </span>
        </div>

        <div className="space-y-6">
          {/* Informações da Semana */}
          <Card>
            <CardHeader>
              <CardTitle>Informações da Semana</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Semana do Programa</p>
                <p className="text-lg font-semibold">{scorecard.semana}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Módulo</p>
                <p className="text-lg font-semibold">{scorecard.modulo}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Período Início</p>
                <p className="text-lg font-semibold">
                  {new Date(scorecard.periodo_inicio).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Período Fim</p>
                <p className="text-lg font-semibold">
                  {new Date(scorecard.periodo_fim).toLocaleDateString('pt-BR')}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Números da Semana */}
          <Card>
            <CardHeader>
              <CardTitle>Números da Semana</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Contatos</p>
                <p className="text-2xl font-bold">{scorecard.metricas?.ligacoes || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Visitas de Captação</p>
                <p className="text-2xl font-bold">{scorecard.metricas?.visitas_1 || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">CRE</p>
                <p className="text-2xl font-bold">{scorecard.metricas?.captacoes || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Estudos de Mercado</p>
                <p className="text-2xl font-bold">{scorecard.metricas?.estudos || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Presença na Silver</p>
                <p className="text-2xl font-bold">{scorecard.presenca_dias || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Posts</p>
                <p className="text-2xl font-bold">{scorecard.metricas?.posts || 0}</p>
              </div>
            </CardContent>
          </Card>

          {/* Ferramentas / Tecnologia */}
          <Card>
            <CardHeader>
              <CardTitle>Ferramentas / Tecnologia</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={scorecard.uso_c2s} disabled className="w-4 h-4" />
                  <span className="text-sm">Uso do C2S</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={scorecard.uso_pipeimob} disabled className="w-4 h-4" />
                  <span className="text-sm">Uso do PipeImob</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={scorecard.uso_ilist} disabled className="w-4 h-4" />
                  <span className="text-sm">Uso do iList</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" checked={scorecard.uso_brokerapp} disabled className="w-4 h-4" />
                  <span className="text-sm">Uso do BrokerApp</span>
                </div>
              </div>
              
              <div className="space-y-4 mt-6">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Pontos de Travamento</p>
                  <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                    {scorecard.pontos_travamento || 'Nenhum ponto de travamento informado'}
                  </p>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Necessidade de Reforço</p>
                  <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                    {scorecard.necessidade_reforco || 'Nenhuma necessidade de reforço informada'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Qualidade e Postura */}
          <Card>
            <CardHeader>
              <CardTitle>Qualidade e Postura (1-5)</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Disciplina</p>
                <p className="text-2xl font-bold">{scorecard.qualidade?.disciplina || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Consistência</p>
                <p className="text-2xl font-bold">{scorecard.qualidade?.consistencia || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Postura Profissional</p>
                <p className="text-2xl font-bold">{scorecard.qualidade?.postura_profissional || 0}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Evolução</p>
                <p className="text-2xl font-bold">{scorecard.qualidade?.evolucao || 0}</p>
              </div>
            </CardContent>
          </Card>

          {/* Resumo da Semana */}
          <Card>
            <CardHeader>
              <CardTitle>Resumo da Semana</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">O que funcionou bem?</p>
                <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                  {scorecard.resumo?.o_que_funcionou || 'Não informado'}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">O que não funcionou?</p>
                <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                  {scorecard.resumo?.o_que_nao_funcionou || 'Não informado'}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">O que fará diferente na próxima semana?</p>
                <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                  {scorecard.resumo?.o_que_fara_diferente || 'Não informado'}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-2">Dúvidas</p>
                <p className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
                  {scorecard.resumo?.duvidas || 'Nenhuma dúvida'}
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Metas Próxima Semana */}
          {scorecard.metas_proxima_semana && (
            <Card>
              <CardHeader>
                <CardTitle>Metas da Próxima Semana</CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Contatos</p>
                  <p className="text-xl font-bold">{scorecard.metas_proxima_semana.ligacoes || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Visitas de Captação</p>
                  <p className="text-xl font-bold">{scorecard.metas_proxima_semana.visitas_1 || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">CRE</p>
                  <p className="text-xl font-bold">{scorecard.metas_proxima_semana.captacoes || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Estudos</p>
                  <p className="text-xl font-bold">{scorecard.metas_proxima_semana.estudos || 0}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Posts</p>
                  <p className="text-xl font-bold">{scorecard.metas_proxima_semana.posts || 0}</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
